import lodashPick from 'lodash/fp/pick';
import queryString, { stringify } from 'querystring';

import typeCheck from 'common/util/typeCheck';
import { USER_ROLE_ANONYMOUS } from 'common/userRoles';

import {
  SR_API_ACTION_PARTNER_TOUR_REQUEST_CREATE,
} from 'api/actionTypes';

import {
  getSelfSchedulingPayload,
  getOrderedToursWithStatuses,
} from 'common/tourHelpers';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
} from 'common/tourConstants';

import {
  PARTNER_BILLING_PLAN_BASE,
} from 'common/partnerConstants';

import requestShowingAction from 'client/actions/Tour/requestShowing';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';
import setAddToTourListingAction from 'client/actions/Tour/setAddToTourListing';
import getToursListAction from 'client/actions/Tour/getList';
import getListingById from 'client/actions/Listing/getById';
import getPartnerById from 'client/actions/Partner/getById';

export default async function requestShowingStepSubmit(
  { state, history, apiClient },
  { listingId, requestShowingStateUpdates, locationQuery },
) {
  typeCheck('listingId::PositiveNumber', listingId);
  typeCheck('history::Object', history);
  typeCheck('requestShowingStateUpdates::Maybe Object', requestShowingStateUpdates);
  typeCheck('locationQuery::Maybe Object', locationQuery);

  // Update the request showing state by listing ID, if a state update is requested.
  if (requestShowingStateUpdates) {
    if (!state.exists(['requestShowingStateByListingId', listingId])) {
      state.set(['requestShowingStateByListingId', listingId], {});
    }
    Object.keys(requestShowingStateUpdates).forEach((requestShowingStateUpdateKey) => {
      state.set(
        ['requestShowingStateByListingId', listingId, requestShowingStateUpdateKey],
        requestShowingStateUpdates[requestShowingStateUpdateKey]
      );
    });
  }

  // Reload the user tours to account for situations where CS updates tour statuses before the user refreshes the page.
  const currentUser = state.get(['currentUser']);
  if (!currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
    await getToursListAction({ state, apiClient }, { reloadTours: true, queryId: listingId });
  }
  const tours = state.get(['tours']);

  const getLocationQuery = () => lodashPick([
    'date',
    'times',
    'requestedFrom',
  ], locationQuery);
  const locationSearch = stringify(getLocationQuery());
  const createNewTour = locationQuery && locationQuery.createNewTour;
  const addToTourId = locationQuery && locationQuery.addToTourId;
  const needsRescheduling = locationQuery && locationQuery.needsRescheduling;
  const requestedFrom = locationQuery && locationQuery.requestedFrom || null;

  const userHasRequestedTour = getOrderedToursWithStatuses(tours, [
    TOUR_STATUS_REQUESTED,
  ]).length > 0;

  const userHasScheduledTour = getOrderedToursWithStatuses(tours, [
    TOUR_STATUS_SCHEDULED,
  ]).length > 0;

  if (
    !currentUser.roles.includes(USER_ROLE_ANONYMOUS)
    && !currentUser.phoneVerified
  ) {
    history.push(`/listing/${listingId}/verify-phone?source=tour&${locationSearch}`);
  }
  else if (addToTourId && needsRescheduling) {
    const existingListingId = tours.find((tour) => tour.id === addToTourId).listings[0].id;
    await setAddToTourListingAction({ state, apiClient }, {
      listingId: listingId,
    });
    const chooseTimeUrl = requestedFrom
      ? `/tour/${addToTourId}/${existingListingId}/choose-tour-time?listingToAddId=${listingId}`
        + `&requestedFrom=${requestedFrom}`
      : `/tour/${addToTourId}/${existingListingId}/choose-tour-time?listingToAddId=${listingId}`;
    history.push(chooseTimeUrl);
  }
  else if (
    !userHasRequestedTour
    && !locationQuery.times
    && !userHasScheduledTour
  ) {
    history.push(`/listing/${listingId}/choose-tour-time?${locationSearch}`);
  }
  else if (currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
    history.push(`/login?${stringify({
      tourRequested: listingId,
      ...getLocationQuery(),
    })}`);
  }
  else {
    const userId = state.get(['currentUser', 'id']);
    const renterProfile = await loadRenterProfileAction(
      { state, history, apiClient },
      { userId },
    );

    const requestShowingPayload = {
      requestedFrom,
      listingId: listingId,
      createNewTour: createNewTour && !addToTourId,
      addToTourId: addToTourId || null,
      // returns object with { startDate, availableTimes }
      ...getSelfSchedulingPayload(locationQuery),
    };

    if (!renterProfile.isPrequalCompleted) {
      history.push(`/application/prequal-start?${queryString.stringify({
        ...locationQuery,
        listingId: listingId,
      })}`);
    }
    else {
      const listing = await getListingById({ state, apiClient }, listingId);

      if (listing.partnerId) {
        const partner = await getPartnerById({ state, apiClient }, { partnerId: listing.partnerId });

        if (partner && partner.billingPlan === PARTNER_BILLING_PLAN_BASE) {
          const partnerTourRequest = await apiClient.postWithState({
            action: SR_API_ACTION_PARTNER_TOUR_REQUEST_CREATE,
            state: state,
            payload: {
              partnerId: listing.partnerId,
              listingId: listing.id,
              ...getSelfSchedulingPayload(locationQuery),
            },
          });

          history.push(`/listing/${listing.id}?newPartnerTourRequestId=${partnerTourRequest.id}`);
          return;
        }
      }

      await requestShowingAction({ state, history, apiClient }, requestShowingPayload);
      history.push(`/tour?${queryString.stringify({ listingId })}`);
    }
  }
}
